import type { GetStaticProps } from 'next'
import { configApi } from '@src/api/config'
import { storyApi } from '@src/api/story'
import { brandApi } from '@src/api/brand'
import Footer from '@src/components/footer'
import Head from '@src/components/head'
import Nav from '@src/components/nav'
import { pageView } from '@src/lib/page-view'
import { useEffect } from 'react'
import { Feed, Brand, Config, Event, StoryVideoSource } from '@api/types'
import FeedsHero from '@src/components/feeds-hero'
import { eventService } from '@src/api/event'
import { Feeds } from '@src/components/feeds'
import { VOD_HERO_EVENT_ID } from '@src/lib/constants/events'
import { isDatePassed } from '@src/lib/utils'

function useLiveStory(feeds: Feed[]) {
  if (!feeds?.length) return undefined

  const primaryFeed = feeds[0]

  // Find the next upcoming story (not passed) that is published
  const hero = primaryFeed?.stories
    .filter((story) => {
      return isDatePassed(story.date)
    })
    .sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })[0]

  return hero
}

function OnDemand({
  feeds,
  brands,
  event,
  config,
  liveVideo
}: {
  feeds: Feed[]
  brands: Brand[]
  event: Event
  config: Config
  liveVideo: StoryVideoSource
}) {
  useEffect(() => {
    pageView()
  }, [])

  const { image, preview_text } = event?.metadata?.social_share ?? {}

  const liveStory = useLiveStory(feeds)

  const SHOW_HERO = !!(event || liveVideo || liveStory)

  return (
    <>
      <Head title={preview_text} ogImage={image} />
      <div className='flex flex-col overflow-hidden md:mt-p[58px] bg-[#121212]'>
        <div className='z-10 w-full md:fixed'>
          <Nav type='on-demand' />
        </div>
        {SHOW_HERO && (
          <FeedsHero
            event={event}
            liveVideo={liveVideo}
            liveStory={liveStory}
            hideContent={false}
          />
        )}
        <div
          className={
            'relative flex items-center justify-center w-screen md:min-h-[calc(100vh_-_56px)] min-h-[calc(100vh_-_104px)] lg:-mt-16'
          }
        >
          <Feeds
            feeds={feeds}
            brands={brands}
            config={config}
            heroVisible={SHOW_HERO}
            liveStory={liveStory}
          />
        </div>
        <Footer className='bg-[#121212]' image={''} />
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  // get feed config
  const config = await configApi.read('barstool-web')
  // render first few
  const initial = config.videos.feeds.slice(0, 6)
  const feeds = await storyApi.findFeeds(initial)
  // get all brands
  const brands = await brandApi.list()
  // Feed live video
  const liveVideos = await storyApi.findLive({ sort: 'viewers', ignore_time: true })
  const liveVideo = liveVideos?.[0]
  const videoSource = liveVideo ? await storyApi.videoSource(liveVideo?.id) : null
  // Feed hero event
  const event = await eventService.getEvent(VOD_HERO_EVENT_ID)

  return {
    props: {
      feeds,
      brands,
      event,
      config,
      liveVideo:
        liveVideo && videoSource
          ? {
              ...videoSource,
              status: liveVideo?.post_type_meta?.live?.status,
              published_at: liveVideo?.post_type_meta?.live?.datetime
            }
          : null
    },
    revalidate: 1
  }
}

export default OnDemand
